import { FormHandles } from '@unform/core';
import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DefaultActionCreators } from 'reduxsauce';
import { RootState } from 'store';
import { SelectedFilterPages, SelectedFilterActions } from 'store/ducks/filter';
import { PaginateAlertEmailLogsActions } from 'store/ducks/settings/alert-email-logs';
import { PaginateAlertsActions } from 'store/ducks/settings/alerts';
import { PaginateBusinessActions } from 'store/ducks/settings/business';
import { PaginateBusinessUnitsActions } from 'store/ducks/settings/business-unit';
import { PaginateCarriersActions } from 'store/ducks/settings/carriers';
import { PaginateClientTypesActions } from 'store/ducks/settings/client-types';
import { PaginateClientsActions } from 'store/ducks/settings/clients';
import { PaginateCompaniesActions } from 'store/ducks/settings/companies';
import { PaginateDocumentCategoryActions } from 'store/ducks/settings/document-category';
import { PaginateGroupsActions } from 'store/ducks/settings/groups';
import { PaginatePalletTypeActions } from 'store/ducks/settings/pallet-type';
import { PaginatePermissionsActions } from 'store/ducks/settings/permissions';
import { PaginateProductTypeActions } from 'store/ducks/settings/product-type';
import { PaginateProductsActions } from 'store/ducks/settings/products';
import { PaginateSupplierActions } from 'store/ducks/settings/suppliers';
import { PaginateTransitsArActions } from 'store/ducks/settings/transit-time-ar';
import { PaginateUsersActions } from 'store/ducks/settings/users';
import { PaginateVehicleTypesActions } from 'store/ducks/settings/vehicle-types';
import { PaginateVehiclesActions } from 'store/ducks/settings/vehicles';
import { PaginateValePedagioTaxasActions } from 'store/ducks/settings/vale-pedagio-taxas';
import { PaginateZipcodeInternacionalActions } from 'store/ducks/settings/zipcode-internacional';

interface UseFiltersConfig {
  formRef: React.RefObject<FormHandles>;
  fieldConfig?: Record<string, 'date' | 'select'>;
}

export const useFilters = (
  page: SelectedFilterPages,
  config?: UseFiltersConfig
) => {
  const dispatch = useDispatch();

  const { data: dataFilter } = useSelector(
    (state: RootState) => state.selectedFilter
  );

  const setFilter = (filters: any) => {
    dispatch(SelectedFilterActions.success({ [page]: filters }));

    const paginateAction = actions[page];

    if (paginateAction) dispatch(paginateAction.request(filters));
  };

  const setPagination = (pageNumber: number) => {
    if (page === 'none') return;

    const currentFilters = dataFilter[page] || {};
    setFilter({ ...currentFilters, page: pageNumber || 1 });
  };

  const getData = useCallback(() => {
    const paginateAction = actions[page];
    const currentFilters = dataFilter[page] || {};

    if (paginateAction) dispatch(paginateAction.request(currentFilters));
  }, [page, dataFilter]);

  const rehydrateFilters = useCallback(() => {
    if (config?.formRef?.current && dataFilter[page]) {
      config.formRef.current.setData(dataFilter[page]);

      if (config.fieldConfig) {
        Object.entries(config.fieldConfig).forEach(([field, type]) => {
          const value = dataFilter[page][field];

          if (type === 'date' && value) {
            config.formRef.current?.setFieldValue(
              field,
              new Date(value).toISOString().split('T')[0]
            );
          } else if (type === 'select' && !value) {
            config.formRef.current?.setFieldValue(field, '');
          }
        });
      }
    }
  }, [page, config]);

  useEffect(() => {
    rehydrateFilters();
  }, [rehydrateFilters]);

  return {
    getData,
    setFilter,
    setPagination,
    filters: dataFilter[page] || null,
  };
};

const actions: Record<SelectedFilterPages, DefaultActionCreators> = {
  alertEmailLogs: PaginateAlertEmailLogsActions,
  alerts: PaginateAlertsActions,
  business: PaginateBusinessActions,
  businessUnits: PaginateBusinessUnitsActions,
  carriers: PaginateCarriersActions,
  clients: PaginateClientsActions,
  clientTypes: PaginateClientTypesActions,
  companies: PaginateCompaniesActions,
  documentCategory: PaginateDocumentCategoryActions,
  groups: PaginateGroupsActions,
  palletTypes: PaginatePalletTypeActions,
  permissions: PaginatePermissionsActions,
  products: PaginateProductsActions,
  productTypes: PaginateProductTypeActions,
  suppliers: PaginateSupplierActions,
  transitTimeAr: PaginateTransitsArActions,
  users: PaginateUsersActions,
  vehicles: PaginateVehiclesActions,
  vehicleTypes: PaginateVehicleTypesActions,
  valePedagioTaxas: PaginateValePedagioTaxasActions,
  zipcodeInternacionals: PaginateZipcodeInternacionalActions,
  none: {} as DefaultActionCreators,
};
